import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Spinner } from "./ui";

/**
 * The page-level loading look (Plex's): one amber arc spinner at the centre
 * of the viewport, under the floating bar, on bare canvas. Home shows it
 * while the servers are looked up and again while the hubs load; every
 * route that waits for its subject shows the same. Pass `minH` for a
 * section-sized slot under a page's own heading.
 */
export function LoadingState(props: { minH?: string; label?: string }) {
  return (
    <div
      role="status"
      aria-label={props.label ?? "Loading"}
      className={`flex ${props.minH ?? "min-h-[100dvh]"} items-center justify-center text-[var(--rago-accent)]`}
    >
      <Spinner size={28} />
    </div>
  );
}

/** The router's pending fallback: the same spinner on the stage while the
 * root route resolves the session (main.tsx defaultPendingComponent). */
export function RootPending() {
  return (
    <div className="rago-stage">
      <LoadingState />
    </div>
  );
}

/** Shimmer placeholder for a poster grid still loading. */
export function SkeletonGrid(props: { count?: number }) {
  return (
    <div
      aria-hidden
      className="grid gap-x-5 gap-y-7 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(164px,1fr))]"
    >
      {Array.from({ length: props.count ?? 12 }).map((_, i) => (
        <div key={i}>
          <div className="rago-skeleton aspect-[2/3] w-full rounded-[var(--rago-radius-m)]" />
          <div className="rago-skeleton mt-2.5 h-3.5 w-3/4 rounded" />
        </div>
      ))}
    </div>
  );
}

/** The route's subject doesn't exist (or isn't visible to this account). */
export function NotFoundState(props: { backTo?: "/" | "/discover"; backLabel?: string }) {
  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center">
      <h1 className="rago-display mb-3 text-xl">Not found</h1>
      <Link to={props.backTo ?? "/"} className="rago-link text-sm">
        {props.backLabel ?? "Back home"}
      </Link>
    </div>
  );
}

/** A page with nothing in it yet — explain what fills it and offer the way there. */
export function EmptyState(props: {
  icon?: ReactNode;
  title: string;
  action?: ReactNode;
  children?: ReactNode;
  /** Section-sized (a card slot inside a page), not viewport-sized. */
  compact?: boolean;
  /** Non-compact height override, for pages that keep their own heading above. */
  minH?: string;
}) {
  const c = props.compact;
  return (
    <div
      className={`mx-auto flex max-w-xl flex-col items-center justify-center px-6 text-center ${
        c ? "py-12" : `${props.minH ?? "min-h-[70vh]"} py-24`
      }`}
    >
      {props.icon ? (
        <span className={`relative ${c ? "mb-4" : "mb-6"}`}>
          <span
            aria-hidden
            className="absolute -inset-20 rounded-full bg-[radial-gradient(closest-side,rgba(8,217,242,0.08),transparent)]"
          />
          <span
            className={`relative flex items-center justify-center rounded-full border border-[var(--rago-line-1)] bg-white/5 text-[var(--rago-accent)] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] ${
              c ? "h-12 w-12" : "h-16 w-16"
            }`}
          >
            {props.icon}
          </span>
        </span>
      ) : null}
      <h1 className={`rago-display mb-3 ${c ? "text-lg" : "text-2xl"}`}>{props.title}</h1>
      {props.children ? (
        <p
          className={`text-sm leading-relaxed text-[var(--rago-text-2)] ${c ? "mb-4" : "mb-8"}`}
        >
          {props.children}
        </p>
      ) : null}
      {props.action}
    </div>
  );
}
